import { Component, type ReactNode } from "react";
import { RotateCcw } from "lucide-react";
import { useI18n } from "../lib/i18n";

/** Send a client-side crash to the server log (no personal data: message, stack, page, browser). */
export function reportClientError(message: string, stack = "") {
  try {
    const sent = Number(sessionStorage.getItem("nc.errs") ?? "0");
    if (sent >= 5) return;
    sessionStorage.setItem("nc.errs", String(sent + 1));
  } catch {
    /* report anyway */
  }
  fetch("/api/client-errors", {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json", "X-Nutricart": "1" },
    body: JSON.stringify({ message: message.slice(0, 500), stack: stack.slice(0, 2000), path: location.pathname, ua: navigator.userAgent.slice(0, 200) }),
  }).catch(() => {});
}

/** After an update, an open page may ask for code files that no longer exist: a fresh load fixes it. */
const STALE_CODE = /dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch dynamically|module script|MIME type/i;

function reloadOnce() {
  try {
    // Once per page and app version: a later update in the same tab reloads again, a broken page never loops.
    const mark = `${location.pathname}@${document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.src ?? ""}`;
    if (sessionStorage.getItem("nc.reloaded") === mark) return false;
    sessionStorage.setItem("nc.reloaded", mark);
  } catch {
    return false;
  }
  location.reload();
  return true;
}

/**
 * Catches a crash in one page so the app never turns into a blank white screen: it shows a friendly note
 * with a reload button, reports the error, and quietly reloads once when the cause is an app update.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (STALE_CODE.test(String(error?.message)) && reloadOnce()) return;
    reportClientError(String(error?.message ?? error), String(error?.stack ?? ""));
  }

  render() {
    return this.state.error ? <CrashNote /> : this.props.children;
  }
}

function CrashNote() {
  const { t } = useI18n();
  return (
    <main className="page">
      <div className="empty">
        <span style={{ fontSize: "2.6rem" }} aria-hidden="true">🙈</span>
        <h3>{t("error.crashTitle")}</h3>
        <p>{t("error.crashBody")}</p>
        <button className="btn" onClick={() => location.reload()}>
          <RotateCcw size={18} /> {t("error.crashReload")}
        </button>
      </div>
    </main>
  );
}
